import { useEffect, useMemo, useState } from "react";

import { ApiError } from "../api/client.js";
import type { TelemetrySnapshot } from "../api/telemetry.js";
import { ClusterCard, isUnconfigured, resolveStatus, type Cluster } from "../components/ClusterCard.js";
import { Icon } from "../components/Icon.js";
import { SiteMap } from "../components/SiteMap.js";
import { useI18n } from "../i18n.js";
import { useSession } from "../session.js";
import type { ProbeResults } from "../Workspace.js";

/**
 * La griglia degli impianti, portata da `Home.qml`.
 *
 * Sopra la riga dei quattro contatori (impianti attivi, normali, in allarme
 * L1, critici), sotto i riquadri video. I conteggi si ricavano dallo stesso
 * stato che colora le schede: calcolarli a parte darebbe due verita' che
 * divergono, e sarebbe il numero in alto a mentire.
 *
 * Telemetria e stato hardware arrivano dall'alto: la connessione deve
 * sopravvivere al cambio di scheda.
 */
export function Home({
  clusters,
  mode,
  telemetry,
  probe,
  onOpen,
}: {
  /** Gli impianti li carica la sessione: il selettore in barra e il pannello
   *  notifiche ne hanno bisogno anche quando questa schermata non c'e'. */
  clusters: Cluster[];
  mode: "grid" | "sitemap";
  telemetry: TelemetrySnapshot;
  probe: ProbeResults;
  onOpen: (c: Cluster) => void;
}) {
  const { t } = useI18n();
  const { api } = useSession();

  const [streams, setStreams] = useState<Record<string, string>>({});
  const [error, setError] = useState("");

  // Quali impianti hanno davvero una camera lo sa il server, che conosce la
  // configurazione del ritrasmettitore. Mettere un riquadro video su un
  // impianto che non ce l'ha sarebbe un rettangolo nero che sembra una camera
  // guasta.
  useEffect(() => {
    let cancelled = false;
    for (const c of clusters) {
      void api
        .call<{ hls: string }>("clusters.rtspUrl", { id: c.id })
        .then((r) => {
          if (!cancelled) setStreams((s) => ({ ...s, [c.id]: r.hls }));
        })
        .catch((e: unknown) => {
          // 404 = nessuna camera, 503 = ritrasmettitore assente: nessuno dei
          // due e' un guasto da mostrare.
          if (!cancelled && !(e instanceof ApiError)) setError(String(e));
        });
    }
    return () => {
      cancelled = true;
    };
  }, [api, clusters]);

  // I tre bucket sono quelli di `Home.qml`, e due non sono ovvi.
  //
  // **L'irraggiungibile conta fra i critici.** Un impianto che non risponde
  // non e' un impianto in meno da sorvegliare: e' quello di cui non si sa
  // niente. Lasciarlo fuori da tutti e tre farebbe una riga di contatori che
  // non torna — un cluster attivo e nessuno stato — e sarebbe il numero in
  // alto a mentire.
  //
  // **Il non configurato non e' normale.** Senza camera, sensore o strada per
  // il PTZ non c'e' lettura da dire normale: contarlo verde direbbe "tutto a
  // posto" di un impianto che non sta guardando niente.
  const counts = useMemo(() => {
    let normal = 0;
    let warning = 0;
    let critical = 0;
    for (const c of clusters) {
      const s = resolveStatus(c, telemetry[c.id], probe[c.id]);
      if (s === "NORMAL" && !isUnconfigured(c)) normal++;
      else if (s === "WARNING") warning++;
      else if (s === "CRITICAL" || s === "OFFLINE") critical++;
    }
    return { normal, warning, critical };
  }, [clusters, telemetry, probe]);

  // Righe e colonne della griglia: le stesse tabelle di `Home.qml`. Non e'
  // un `auto-fill` di riquadri a larghezza minima — la griglia dell'app
  // desktop **occupa tutta l'area**, e i riquadri si dividono lo spazio.
  // Un impianto solo prende tutto lo schermo, che e' il caso normale di
  // questo prodotto: sei impianti sono sei PC, non sei riquadri.
  const cols = gridCols(clusters.length);
  const rows = gridRows(clusters.length);

  return (
    <div className="home">
      <div className="kpirow">
        <Kpi
          icon="activity"
          value={clusters.length}
          label={t("home.activeClusters")}
          tone="neutral"
        />
        <Kpi icon="shield-check" value={counts.normal} label={t("home.normal")} tone="normal" />
        <Kpi
          icon="alert-triangle"
          value={counts.warning}
          label={t("home.warnings")}
          tone="warning"
        />
        <Kpi icon="shield-alert" value={counts.critical} label={t("home.critical")} tone="critical" />
      </div>

      {error && <p className="alert">{error}</p>}

      {/* L'area sotto i contatori e' una sola, e le due viste ci stanno
          dentro a tutta altezza: nel `.qml` e' un `Item` con
          `Layout.fillHeight`, non una pila che si accatasta in alto. */}
      <div className="home__stage">
        {mode === "sitemap" && (
          <SiteMap clusters={clusters} telemetry={telemetry} probe={probe} onOpen={onOpen} />
        )}

        <div
          className="tilegrid"
          hidden={mode !== "grid"}
          style={{
            gridTemplateColumns: `repeat(${cols}, 1fr)`,
            gridTemplateRows: `repeat(${rows}, 1fr)`,
          }}
        >
          {clusters.map((c) => (
            <ClusterCard
              key={c.id}
              cluster={c}
              telemetry={telemetry[c.id]}
              probe={probe[c.id]}
              streamUrl={streams[c.id]}
              onOpen={() => onOpen(c)}
            />
          ))}
        </div>

        {mode === "grid" && clusters.length === 0 && !error && (
          <div className="home__empty">
            <Icon name="video" size={64} />
            <span>{t("home.initializing")}</span>
          </div>
        )}
      </div>
    </div>
  );
}

function Kpi({
  icon,
  value,
  label,
  tone,
}: {
  icon: string;
  value: number;
  label: string;
  tone: "neutral" | "normal" | "warning" | "critical";
}) {
  return (
    <div className={`kpi kpi--${tone}`}>
      <div className="kpi__icon">
        <Icon name={icon} size={22} />
      </div>
      <div>
        <div className="kpi__value">{value}</div>
        <div className="kpi__label">{label}</div>
      </div>
    </div>
  );
}

/**
 * Quante colonne e quante righe, per numero di impianti.
 *
 * Sono le due tabelle di `Home.qml` copiate voce per voce. Non seguono una
 * formula — 4 impianti fanno 2x2 e non 3+1, 9 fanno 3x3 e non 4+4+1 — perche'
 * a parita' di area un riquadro largo il doppio e alto la meta' mostra meno
 * video utile: la camera inquadra 16:9.
 */
function gridCols(n: number): number {
  if (n <= 1) return 1;
  if (n <= 2) return 2;
  if (n <= 3) return 3;
  if (n <= 4) return 2;
  if (n <= 6) return 3;
  if (n <= 8) return 4;
  if (n <= 9) return 3;
  return 4;
}

function gridRows(n: number): number {
  if (n <= 2) return 1;
  if (n <= 3) return 1;
  if (n <= 8) return 2;
  if (n <= 12) return 3;
  return 4;
}
